class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        cartItems.updateQuantity(this.dataset.index, 0, event);
      } else {
        console.error('CartItems element not found or updateQuantity method not available');
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    // Allow quantity 0 to remove item, otherwise check minimum
    if (inputValue !== 0 && inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }
    // Removed max validation to allow unlimited quantities

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    // Disable HTML5 validation for quantity inputs
    if (event.target.type === 'number' && event.target.name === 'updates[]') {
      event.target.setCustomValidity('');
    }
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // Find the currently visible cart drawer (there should only be one)
          const currentCartDrawer = document.querySelector('cart-drawer');
          if (!currentCartDrawer) {
            console.error('No cart-drawer element found');
            return;
          }

          // Check if cart is empty in the new response
          const sourceEmptyCart = html.querySelector('.drawer__inner-empty');
          const targetDrawerInner = currentCartDrawer.querySelector('.drawer__inner');

          // If cart is now empty, replace entire drawer__inner with empty state
          if (sourceEmptyCart && targetDrawerInner) {
            targetDrawerInner.innerHTML = sourceEmptyCart.innerHTML;
            currentCartDrawer.classList.add('is-empty');
            return;
          }

          // Otherwise, update cart items and footer normally
          const targetCartItems = currentCartDrawer.querySelector('cart-drawer-items');
          const sourceCartItems = html.querySelector('cart-drawer-items');
          if (targetCartItems && sourceCartItems) {
            targetCartItems.innerHTML = sourceCartItems.innerHTML;
          }

          const targetFooter = currentCartDrawer.querySelector('.drawer__footer');
          const sourceFooter = html.querySelector('.drawer__footer');
          if (targetFooter && sourceFooter) {
            targetFooter.innerHTML = sourceFooter.innerHTML;
          }

          // Re-disable quantity validation after cart drawer refresh
          setTimeout(() => {
            this.disableQuantityValidation();
          }, 100);
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    return [
      {
        id: 'main-cart-items',
        section: mainCartItems?.dataset?.id || 'main-cart-items',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: mainCartFooter?.dataset?.id || 'main-cart-footer',
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        // Override server-side quantity limits
        if (parsedState.items && parsedState.items[line - 1]) {
          if (parsedState.items[line - 1].quantity !== quantity) {
            // Force the quantity to what was requested
            parsedState.items[line - 1].quantity = quantity;

            // Recalculate line price
            const itemPrice = parsedState.items[line - 1].price;
            parsedState.items[line - 1].line_price = itemPrice * quantity;
            parsedState.items[line - 1].original_line_price = itemPrice * quantity;
            parsedState.items[line - 1].final_line_price = itemPrice * quantity;

            // Recalculate total
            parsedState.total_price = parsedState.items.reduce(
              (total, item) => total + item.line_price,
              0,
            );
            parsedState.original_total_price = parsedState.items.reduce(
              (total, item) => total + item.original_line_price,
              0,
            );
          }
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper)
            cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            if (!parsedState.sections[section.section]) {
              return;
            }

            const sectionElement = document.getElementById(section.id);
            if (!sectionElement) {
              return;
            }

            const elementToReplace =
              sectionElement.querySelector(section.selector) || sectionElement;

            if (elementToReplace) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector,
              );

              // Re-disable quantity validation after DOM update
              this.disableQuantityValidation();
            }
          });

          const updatedValue = parsedState.items[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;
          let message = '';

          // Force the quantity input to show the requested quantity (not server response)
          if (quantityElement && quantityElement.value !== quantity.toString()) {
            quantityElement.value = quantity;
          }

          // Hide any quantity limit error messages
          const errorElement = document.getElementById(`CartDrawer-LineItemError-${line}`);
          if (errorElement) {
            errorElement.style.display = 'none';
            const errorText = errorElement.querySelector('.cart-item__error-text');
            if (errorText) {
              errorText.textContent = '';
            }
          }

          if (
            items.length === parsedState.items.length &&
            updatedValue !== parseInt(quantityElement.value)
          ) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) ||
            document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(
              cartDrawerWrapper.querySelector('.drawer__inner-empty'),
              cartDrawerWrapper.querySelector('a'),
            );
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) =>
          overlay.classList.add('hidden'),
        );
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden'),
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  disableQuantityValidation() {
    const quantityInputs = document.querySelectorAll('input[type="number"][name="updates[]"]');
    quantityInputs.forEach((input) => {
      input.removeAttribute('max');
      input.setCustomValidity('');
      input.addEventListener('invalid', (e) => {
        e.preventDefault();
        e.target.setCustomValidity('');
      });
    });
  }
}

customElements.define('cart-items', CartItems);

// Global handler to prevent HTML5 validation errors on quantity inputs
document.addEventListener('DOMContentLoaded', () => {
  const disableQuantityValidation = () => {
    const quantityInputs = document.querySelectorAll('input[type="number"][name="updates[]"]');
    quantityInputs.forEach((input) => {
      input.removeAttribute('max');
      input.setCustomValidity('');
      input.addEventListener('invalid', (e) => {
        e.preventDefault();
        e.target.setCustomValidity('');
      });
    });
  };

  // Run on page load
  disableQuantityValidation();

  // Run when cart drawer opens
  document.addEventListener('cart:updated', () => {
    setTimeout(disableQuantityValidation, 50);
  });

  // Run when cart drawer content changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const quantityInputs = node.querySelectorAll
              ? node.querySelectorAll('input[type="number"][name="updates[]"]')
              : [];
            quantityInputs.forEach((input) => {
              input.removeAttribute('max');
              input.setCustomValidity('');
              input.addEventListener('invalid', (e) => {
                e.preventDefault();
                e.target.setCustomValidity('');
              });
            });
          }
        });
      }
    });
  });

  // Observe cart drawer for changes
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

  // Override form validation for cart forms
  const cartForms = document.querySelectorAll('form[id*="Cart"]');
  cartForms.forEach((form) => {
    form.addEventListener('submit', (e) => {
      // Prevent HTML5 validation
      e.preventDefault();

      // Remove max attributes from all quantity inputs
      const quantityInputs = form.querySelectorAll('input[type="number"][name="updates[]"]');
      quantityInputs.forEach((input) => {
        input.removeAttribute('max');
        input.setCustomValidity('');
      });

      // Allow the form to submit normally
      form.submit();
    });
  });
});

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event),
            );
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
